import './RegisterModalExecutor.scss';
import React, { useEffect, useState } from 'react';
import { IDocumentModalState } from '@/store/reducers/DocumentReducer';
import { useNavigate } from 'react-router';
import { DocumentService } from '@services/DocumentService';
import { IDocumentData } from '@models/document/IDocumentData';
import DocumentModal from '@organisms/DocumentModal';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';
import { IRegisterModalExecutorProps } from './IRegisterModalExecutorProps';
import { RouteNames } from '@/router/router.types';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { stringifyError } from '@srm/core/errors';

export const RegisterModalExecutorModal: React.FC<IRegisterModalExecutorProps> = (
    props: IRegisterModalExecutorProps,
) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [documentState, setDocumentState] = useState<IDocumentModalState>();
    const [savedDocumentUrl, setSavedDocumentUrl] = useState<string>();
    const [saving, setSaving] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setIsLoading(true);
        Promise.all([
            DocumentService.getSchemeByAction(props.actionKey, props.docId),
            DocumentService.getDataByAction(props.actionKey, props.docId),
        ])
            .then((values) => {
                if (ignore) return;
                const scheme = values[0];
                const data = values[1];
                setDocumentState({
                    scheme: scheme.data,
                    data: data.data,
                });
                setIsLoading(false);
            })
            .catch((error: unknown) => {
                if (ignore) return;
                // Без схемы форму не показать: окно закрывается отказом, а не висит скелетоном.
                sendModalMsg({ message: Array.isArray(error) ? error : [stringifyError(error)], type: 'error' });
                props.completeHandler?.(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const saveDocument = async (data: IDocumentData) => {
        data.baseDocKey = props.docId || undefined;
        setSaving(true);
        try {
            const result = await DocumentService.saveData(data);
            return RouteNames.DOCUMENT.replace(':id', result.data.id);
        } finally {
            setSaving(false);
        }
    };

    const onSaveDocumentModal = async (data: IDocumentData) => {
        await saveDocument(data);
        props.completeHandler?.(true);
    };

    const onSaveAndOpenDocumentModal = async (data: IDocumentData) => {
        const documentUrl = await saveDocument(data);
        if (props.openInNewTab) {
            setSavedDocumentUrl(documentUrl);
        } else {
            void navigate(documentUrl);
            props.completeHandler?.(true);
        }
    };

    const onCancelDocumentModal = () => {
        props.completeHandler && props.completeHandler(false);
    };

    if (savedDocumentUrl) {
        return (
            <Modal
                size="m"
                header="Документ сохранён"
                onClose={() => props.completeHandler?.(true)}
                footer={<Button size="sm" onClick={() => props.completeHandler?.(true)}>Закрыть</Button>}
            >
                <a
                    href={savedDocumentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Открыть документ в новой вкладке
                </a>
            </Modal>
        );
    }

    return (
        <DocumentModal
            scheme={documentState?.scheme}
            data={documentState?.data}
            modalSize={props.modalSize}
            saveButtonText={props.saveButtonText}
            saveAndOpenButtonText={props.openInNewTab ? 'Сохранить и открыть' : 'Сохранить и перейти'}
            isLoading={isLoading}
            isEdit={false}
            isNew={true}
            onSave={onSaveDocumentModal}
            onSaveAndOpen={onSaveAndOpenDocumentModal}
            onCancel={onCancelDocumentModal}
            fullHeight={false}
            closeDisabled={saving}
        />
    );
};
